import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Button, Input, Text } from '@kula-ai/components';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import type { ClarifyFormBlockData } from '../../../types';

interface ClarifyFormBlockProps {
  data: ClarifyFormBlockData;
}

export const ClarifyFormBlock = ({ data }: ClarifyFormBlockProps) => {
  const { send, isSending } = useChatSubmit();
  const isStreaming = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    if (!sid) return false;
    const tab = s.openTabs.find((t) => t.sessionId === sid);
    return tab?.status === 'streaming' || s.streamingDraft?.sessionId === sid;
  });
  const disabled = isSending || isStreaming;

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(data.actions.map((a) => [a.key, a.prefill]))
  );

  const handleSubmit = () => {
    if (disabled) return;
    const message = data.actions.reduce(
      (tpl, field) =>
        tpl.replace(
          new RegExp(`\\{${field.key}\\}`, 'g'),
          values[field.key] ?? ''
        ),
      data.warning
    );
    void send(message);
  };

  return (
    <Box
      data-test-id="agentforge-clarify-form-block"
      bg="base-white"
      border="1px solid"
      borderColor="primary-200"
      borderRadius="10px"
      overflow="hidden"
    >
      <Box
        px="14px"
        py="10px"
        borderBottom="1px solid"
        borderColor="primary-100"
        bg="primary-25"
      >
        <Text variant="span" className="text-primary-900 font-semibold">
          {data.intro}
        </Text>
      </Box>

      <Flex direction="column" gap="10px" px="14px" py="12px">
        {data.actions.map((field) => (
          <Box key={field.key}>
            <Text
              variant="span"
              className="text-secondary-700 text-sm font-medium"
            >
              {field.label}
            </Text>
            <Box mt="4px">
              <Input
                value={values[field.key] ?? ''}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    [field.key]: e.target.value,
                  }))
                }
                disabled={disabled}
                size="sm"
              />
            </Box>
          </Box>
        ))}
      </Flex>

      <Box
        px="14px"
        py="10px"
        borderTop="1px solid"
        borderColor="secondary-100"
      >
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          isDisabled={disabled}
        >
          {data.submitLabel}
        </Button>
      </Box>
    </Box>
  );
};
